import React, { FC, useRef, useReducer, Reducer, useEffect, useState } from 'react';
import MP4Box, { Mp4BoxInfo } from 'mp4box';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import ModalHeader from '../ModalHeader';
import ModalFooter from '../ModalFooter';
import ProgressBar from '../ProgressBar';
import { getEncoderProfiles, getSignedUrl, uploadFile } from './api';
import {
  reducer,
  initialState,
  validateVideoType,
  validateDimensions,
  validateRequiredField,
  validateSubmit,
  validateFileType,
  validateFileSize,
  validateDuration,
  validateAudioType,
  validateMaxAudioChannels,
  validateAudioBitrate,
  validateMaxVideoChannels,
  validateMaxVideoBitrate,
  validateMaxResolution,
  validateSampleRate,
  validateUploadFileHasAudio,
  validations,
  validateFps,
  sliceLargeBlob,
  logSampleDuration,
  logTimescale,
  calculateGop,
  ChecksumTask,
  computeChecksum
} from './eventUploadHelpers';
import './style.scss';
import { EventUploadState, Action, ValidationsObject, ACTION_TYPE, VALIDATIONS, WebEventProfile, SignedUrlResponse } from './eventUploadTypes';
import { UploadFileRequirements } from '..';
import { convertProgressSize } from '../../helpers/upload';

export interface EventUploadModalProps {
  closeFunction(): void;
  customerId: string;
  disableMp4BoxValidations: boolean;
}

const EventUploadModal: FC<EventUploadModalProps> = ({ closeFunction, customerId, disableMp4BoxValidations = false }): JSX.Element => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const checksumTaskRef = useRef<ChecksumTask | null>(null);
  const [source, setSource] = useState<any>(null);
  const [state, dispatch] = useReducer<Reducer<EventUploadState, Action>>(reducer, initialState);
  const mp4boxFile = useRef(MP4Box.createFile());
  const activeReaders = useRef<FileReader[]>([]);
  const anyReaderAborted = useRef(false);

  function beforeUnloadEvent(e: any) {
    const preferredMessage = 'Your upload will be lost if you leave this window.';
    (e || window.event).returnValue = preferredMessage;
    return preferredMessage;
  }

  function updateError(type: VALIDATIONS, invalid = true) {
    const errorsClone: ValidationsObject = { ...state.errors };
    errorsClone[type].invalid = invalid;
    dispatch({
      type: ACTION_TYPE.ERROR_LIST,
      payload: { ...errorsClone },
    })
  }

  const resetValidations = () => Object.values(validations).forEach(x => x.invalid = false);

  function openFileExplorer() {
    updateError(VALIDATIONS.BACK_END, false);
    if (fileRef?.current?.click) {
      fileRef.current.click();
    }
  }

  function validateAndLogMp4BoxInfo(info: Mp4BoxInfo) {
    console.log(info);

    const updateErrorAndCancelChecksum = (type: VALIDATIONS, invalid?: boolean): void => {
      if (invalid) {
        checksumTaskRef.current?.cancel();
      }
      updateError(type, invalid);
    };

    calculateGop(mp4boxFile.current, info, updateErrorAndCancelChecksum, dispatch);
    validateFps(info, updateErrorAndCancelChecksum);
    validateUploadFileHasAudio(info, updateErrorAndCancelChecksum);
    validateMaxResolution(info, updateErrorAndCancelChecksum);
    validateMaxVideoBitrate(info, updateErrorAndCancelChecksum);
    validateMaxVideoChannels(info, updateErrorAndCancelChecksum);
    validateAudioBitrate(info, updateErrorAndCancelChecksum);
    validateSampleRate(info, updateErrorAndCancelChecksum);
    validateMaxAudioChannels(info, updateErrorAndCancelChecksum);
    validateAudioType(info, updateErrorAndCancelChecksum);
    validateVideoType(info, updateErrorAndCancelChecksum);
    validateDimensions(info, updateErrorAndCancelChecksum);
    validateDuration(info, updateErrorAndCancelChecksum);
    validateFileSize(info, updateErrorAndCancelChecksum);
    logSampleDuration(info);
    logTimescale(info);
    // Release the references to the ArrayBuffers associated w/ the mp4boxFile
    mp4boxFile.current = MP4Box.createFile();
  }

  const hasNoErrors = (): boolean => Object.values(state.errors).every(({ invalid }) => !invalid);

  function handleFileChange(e: any) {
    const [videoFile] = e.target.files;
    const fileName = videoFile ? videoFile.name : '';
    const mime = videoFile ? videoFile.type : '';
    mp4boxFile.current = MP4Box.createFile();
    activeReaders.current.length = 0;
    anyReaderAborted.current = false
    let loadedBlobSize = 0;

    // reset value of hidden input so user can
    // select same file twice in a row
    if (fileRef?.current?.value) {
      fileRef.current.value = '';
    }
    checksumTaskRef.current?.cancel();

    updateError(VALIDATIONS.MAX_GOP, false);
    dispatch({ type: ACTION_TYPE.ANALYZING_TOTAL, payload: videoFile.size });
    dispatch({ type: ACTION_TYPE.FILE_NAME, payload: fileName });
    dispatch({ type: ACTION_TYPE.UPLOAD_PROGRESS, payload: 0 });
    dispatch({ type: ACTION_TYPE.FILE_CHECKSUM_PROGRESS, payload: 0 });
    dispatch({ type: ACTION_TYPE.FILE_CHECKSUM, payload: null });
    dispatch({ type: ACTION_TYPE.SET_ANALYSIS_RUNNING, payload: true });
    dispatch({ type: ACTION_TYPE.SET_CHECKSUM_TASK_RUNNING, payload: true });

    if (!videoFile) {
      updateError(VALIDATIONS.NO_UPLOAD_FILE)
    } else {
      updateError(VALIDATIONS.NO_UPLOAD_FILE, false)
    }

    if (!mime.includes('video')) {
      updateError(VALIDATIONS.NOT_A_VIDEO);
    } else {
      updateError(VALIDATIONS.NOT_A_VIDEO, false);
    }

    validateFileType(fileName, updateError);

    mp4boxFile.current.onError = (e: any) => {
      console.log(e);
      updateError(VALIDATIONS.INVALID_FORMAT);
      return;
    }

    const blobFile = new Blob([videoFile], { type: "video/mp4" });

    function addToBuffer(blob: Blob, pos: number): Promise<void> {
      const fileReader = new FileReader();

      if (anyReaderAborted.current) {
        return Promise.resolve();
      }

      activeReaders.current.push(fileReader);

      return new Promise((resolve, reject) => {
        fileReader.onload = (e: any) => {
          const buffer = e?.target?.result;
          buffer.fileStart = pos;
          mp4boxFile.current.appendBuffer(buffer);

          const eventSize = e?.target?.result?.byteLength || 0;
          loadedBlobSize += eventSize;

          // also see useEffect for [state.loading, state.analyzing].. separate because of the way
          // react state updates
          if (eventSize && loadedBlobSize / videoFile.size >= 1) {
            if (disableMp4BoxValidations) {
              calculateGop(mp4boxFile.current, mp4boxFile.current.getInfo(), updateError, dispatch, false)
            } else {
              validateAndLogMp4BoxInfo(mp4boxFile.current.getInfo());
            }
            activeReaders.current.length = 0;

            dispatch({ type: ACTION_TYPE.SET_ANALYSIS_RUNNING, payload: false });
          }

          dispatch({ type: ACTION_TYPE.ANALYZING_LOADED, payload: loadedBlobSize });
          resolve();
        }
        fileReader.onabort = () => anyReaderAborted.current = true;
        fileReader.onerror = () => {
          console.error(fileReader.error);
          reject(fileReader.error);
        };
        fileReader.readAsArrayBuffer(blob);
      });
    }

    const checksumTask = computeChecksum(blobFile, checksumProgress => {
      dispatch({ type: ACTION_TYPE.FILE_CHECKSUM_PROGRESS, payload: checksumProgress });
    });

    checksumTaskRef.current = checksumTask;
    checksumTask.hash().then(checksum => {
      console.log(`File checksum: ${checksum}`);
      dispatch({ type: ACTION_TYPE.FILE_CHECKSUM, payload: checksum });
    }).catch(e => {
      console.error("Checksum error", e);
      updateError(VALIDATIONS.BACK_END);
    }).finally(() => {
      checksumTaskRef.current = null;
      dispatch({ type: ACTION_TYPE.SET_CHECKSUM_TASK_RUNNING, payload: false });
    });

    sliceLargeBlob(videoFile, addToBuffer);

    dispatch({ type: ACTION_TYPE.FILE, payload: blobFile });
  }


  const enableDone = (): boolean => {
    const { isUploading, uploadProgress, uploadTotal, analyzingLoaded, analyzingTotal, fileChecksumProgress } = state;

    if (isUploading) {
      return false;
    }
    if (Math.floor(analyzingLoaded / analyzingTotal) >= 1 && fileChecksumProgress >= analyzingTotal && hasNoErrors()) {
      return true;
    }
    if (Math.floor(uploadProgress / uploadTotal) >= 1) {
      return true;
    }
    return false;
  }

  const toPercent = (progress: number, total: number): number => Math.floor(100 * (progress / total)) || 0;

  const buttonContent = (): JSX.Element => {
    const { uploadProgress, analyzingLoaded, isAnalysisRunning, isChecksumTaskRunning } = state;
    if (isAnalysisRunning || isChecksumTaskRunning) {
      return <i className="fa fa-spinner fa-spin" aria-hidden="true" style={{ marginRight: 7 }}></i>;
    }
    if (uploadProgress === 0 && analyzingLoaded > 0 && hasNoErrors()) {
      return <>Upload</>
    }
    return <>Done</>
  }

  const removeFields = () => {
    resetValidations();

    dispatch({ type: ACTION_TYPE.FILE, payload: null });
    dispatch({ type: ACTION_TYPE.FILE_NAME, payload: '' });
    dispatch({ type: ACTION_TYPE.FILE_CHECKSUM, payload: null });
    dispatch({ type: ACTION_TYPE.FILE_CHECKSUM_PROGRESS, payload: 0 });
    dispatch({ type: ACTION_TYPE.ERROR_LIST, payload: validations });
    dispatch({ type: ACTION_TYPE.IS_UPLOADING, payload: false });
    dispatch({ type: ACTION_TYPE.UPLOAD_PROGRESS, payload: 0 });
    dispatch({ type: ACTION_TYPE.ANALYZING_LOADED, payload: 0 });
    dispatch({ type: ACTION_TYPE.ANALYZING_TOTAL, payload: 0 });
    dispatch({ type: ACTION_TYPE.UPLOAD_TOTAL, payload: 0 });
    dispatch({ type: ACTION_TYPE.SIGNED_URL_RES, payload: null });
    dispatch({ type: ACTION_TYPE.SET_ANALYSIS_RUNNING, payload: false });
    dispatch({ type: ACTION_TYPE.SET_CHECKSUM_TASK_RUNNING, payload: false });
  }

  const closeModal = () => {
    const leaveConfirmed = state.isUploading ? window.confirm('If you leave this window before your upload is finished, your upload will be cancelled. Are you sure you want to exit this modal?') : true;
    if (!leaveConfirmed) {
      return;
    }

    window.removeEventListener('beforeunload', beforeUnloadEvent);
    checksumTaskRef.current?.cancel();
    closeFunction();
  }

  async function handleSubmit() {
    dispatch({ type: ACTION_TYPE.UPLOAD_CANCELED, payload: false });
    if (state.uploadProgress >= 100) {
      closeModal();
      return;
    }
    if (!validateSubmit(state, updateError) || !hasNoErrors()) {
      return;
    }
    if (state.isUploading || !state.file || !state.fileChecksum) {
      console.warn('Submit blocked: upload is loading, there is no file, or the checksum was not set');
      return;
    }
    if (state.file.size === 0) {
      updateError(VALIDATIONS.FILE_SIZE_0);
      return;
    }

    if (state.fileChecksum === null) {
      throw new Error("fileChecksum was not set; the FILE_CHECKSUM action must be dispatched");
    }

    window.addEventListener('beforeunload', beforeUnloadEvent);

    const signedUrlResponse: SignedUrlResponse = await getSignedUrl(customerId, state.eventProfile,
      JSON.stringify({ eventName: state.eventName, fileName: state.fileName, gopSeconds: state.gopSeconds, crc32c: state.fileChecksum }));

    dispatch({ type: ACTION_TYPE.SIGNED_URL_RES, payload: signedUrlResponse });
    dispatch({ type: ACTION_TYPE.IS_UPLOADING, payload: true });

    try {
      await uploadFile({
        file: state.file,
        checksumBase64: state.fileChecksum,
        uploadUrl: signedUrlResponse.uploadUrl,
        dispatch,
        setSource,
      })
    }
    catch (e) {
      // checking e.request prevents an error from appearing on cancel
      if (e.request) {
        e.status ? updateError(VALIDATIONS.BACK_END) : updateError(VALIDATIONS.NETWORK_ERROR);
      }
      console.error(e);
    }
    dispatch({ type: ACTION_TYPE.IS_UPLOADING, payload: false });
    dispatch({ type: ACTION_TYPE.UPLOAD_FINISHED, payload: true });
    window.removeEventListener('beforeunload', beforeUnloadEvent);
  }

  const handleEventProfile = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    validateRequiredField(value, VALIDATIONS.BLANK_EVENT_PROFILE, updateError);
    dispatch({ type: ACTION_TYPE.PROFILE, payload: value });
  }

  const handleEventName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    validateRequiredField(value, VALIDATIONS.BLANK_EVENT_NAME, updateError);
    dispatch({ type: ACTION_TYPE.NAME, payload: value })
  }

  const cancelUpload = () => {
    checksumTaskRef.current?.cancel();
    checksumTaskRef.current = null;
    dispatch({ type: ACTION_TYPE.UPLOAD_CANCELED, payload: true });
    if (source?.cancel) {
      source.cancel();
    }
    removeFields();
    activeReaders.current.forEach(x => x.abort());
  }

  const hideCancelButton = (): boolean => {
    const amountAnalyzed = state.analyzingLoaded / state.analyzingTotal || 0;
    const amountUploaded = state.uploadProgress / state.uploadTotal || 0;
    if (amountUploaded >= 100) {
      return true;
    }
    // If analyzing is finished, but upload has not started, the user should not be able to cancel
    // because the upload will soon start and not be cancelable.
    if (amountAnalyzed === 0 || (amountAnalyzed >= 1 && amountUploaded === 0) || (state.fileChecksumProgress >= state.analyzingTotal && amountUploaded === 0)) {
      return true;
    }
    return false;
  }

  useEffect(() => {

    (async () => {
      const encoderProfiles: WebEventProfile[] = await getEncoderProfiles();
      encoderProfiles.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1);
      dispatch({ type: ACTION_TYPE.PROFILE_LIST, payload: encoderProfiles });
    })();

    return () => window.removeEventListener('beforeunload', beforeUnloadEvent);
  }, [])

  useEffect(() => {
    if (state.analyzingTotal > 0 && state.analyzingLoaded / state.analyzingTotal >= 1 && state.fileChecksum !== null) {
      handleSubmit();
    }
  }, [state.analyzingLoaded, state.analyzingTotal, state.fileChecksum]);

  useEffect(() => {
    if (state.uploadFinished) {
      if (!state.uploadCanceled && state.uploadProgress >= 100) {

        if (!state.signedUrlResponse) {
          console.error('Signed Url is null')
        }

      } else if (!state.uploadCanceled) {
        updateError(VALIDATIONS.BACK_END);
      }
      dispatch({ type: ACTION_TYPE.UPLOAD_FINISHED, payload: false });
      dispatch({ type: ACTION_TYPE.UPLOAD_CANCELED, payload: false });
    }
  }, [state.uploadFinished])

  return (
    <Modal width="900px">
      <ModalHeader onClose={closeModal} closeButton={true} titleIconClass="fa fa-cog la1-evt-upload-cog" titleText="Upload" />
      <div className="la1-evt-upload-space" />
      <div className="la1-evt-upload">
        <div className="la1-evt-upload-link-wrap">
          <span
            className="la1-evt-upload-link"
            tabIndex={0} role="link"
            onKeyPress={e => e.key === 'Enter' ? dispatch({ type: ACTION_TYPE.SHOW_FILE_REQUIREMENTS, payload: !state.showFileRequirements }) : null}
            onClick={() => dispatch({ type: ACTION_TYPE.SHOW_FILE_REQUIREMENTS, payload: !state.showFileRequirements })}>
            {state.showFileRequirements ? 'Hide' : 'View'} File Requirements
          </span>
        </div>
        <div className="la1-evt-upload-space"></div>
        <UploadFileRequirements visible={state.showFileRequirements} />
        <div className="la1-evt-upload-space-between">
          <div className="la1-evt-upload-half-width-wrapper">
            <label className="control-label la1-evt-upload-label" htmlFor="event-profile">Event Profile</label>
            {/* eslint-disable-next-line jsx-a11y/no-onchange */}
            <select value={state.eventProfile} disabled={state.isUploading} onChange={handleEventProfile} id="event-profile" data-testid="event-profiles-select" className="form-control la1-evt-upload-input">
              <option value="" disabled></option>
              {state.profileList.map(x => <option value={x.uuid} key={x.uuid} data-testid={x.name}>{x.name}</option>)}
            </select>
          </div>
          <div className="la1-evt-upload-half-width-wrapper">
            <label className="control-label la1-evt-upload-label" htmlFor="event-name">Event Name</label>
            <input onChange={handleEventName} disabled={state.isUploading} className="form-control la1-evt-upload-input" id="event-name" data-testid="event-name-input" />
          </div>
        </div>
        <div className="la1-evt-upload-space-between">
          <div className="la1-evt-upload-half-width-wrapper">
            <label className="control-label la1-evt-upload-label" htmlFor="file-upload">File Upload</label>
            <div className="la1-evt-upload-upload-wrap">
              <button className={!state.isUploading ? "btn btn-primary la1-evt-upload-upload-btn" : "btn btn-primary la1-evt-upload-gray-btn"} disabled={state.isUploading} onClick={openFileExplorer} data-testid="upload-choose-file-btn">
                Choose File
              </button>
              <input value={state.fileName ? '' + state.fileName : ''}
                className="form-control la1-evt-upload-input"
                type="text"
                placeholder="No file chosen"
                id="file-upload"
                data-testid="filename-upload"
                accept="video/*"
                readOnly />
            </div>
          </div>
          <div className="la1-evt-upload-half-width-wrapper">
            <div className="la1-evt-upload-flex-progress-text">
              <div className="la1-evt-upload-progress-bars-wrap">
                <div className="la1-evt-upload-progress-bar-wrap">
                  <div className="control-label la1-evt-upload-progress-label">Analyzing</div>
                  <ProgressBar progressUpdate={toPercent((state.fileChecksumProgress + state.analyzingLoaded) / 2, state.analyzingTotal)} color="#605ca8" />
                  <div className="la1-evt-upload-progress-txt la1-evt-upload-progress-txt--more-left">
                    ({convertProgressSize((state.fileChecksumProgress + state.analyzingLoaded) / 2)} of {convertProgressSize(state.analyzingTotal)})
                  </div>
                </div>
                <div className="la1-evt-upload-progress-bar-wrap">
                  <div className="control-label la1-evt-upload-progress-label">Uploading</div>
                  <ProgressBar progressUpdate={toPercent(state.uploadProgress, state.uploadTotal)} color="#3c8dbc" />
                  <div className="la1-evt-upload-progress-txt la1-evt-upload-progress-txt--more-left">
                    ({convertProgressSize(state.uploadProgress)} of {convertProgressSize(state.uploadTotal)})
                  </div>
                </div>
              </div>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
              <div
                onClick={cancelUpload}
                role="button"
                tabIndex={0}
                style={{ visibility: hideCancelButton() ? 'hidden' : 'unset' }}
                className="la1-evt-upload-progress-txt la1-evt-upload-progress-txt--cancel"
                data-testid="cancel-upload-txt"
              >
                CANCEL
              </div>
            </div>
          </div>
        </div>
        <div className="la1-evt-upload-m-t-1"><a href="https://resi.io/documentation/file-upload" target="_blank" rel="noopener noreferrer">Upload Video Documentation</a></div>
        <div className="la1-evt-upload-space" />
        {Object.values(state.errors).map(({ invalid, message, key }) => {
          return invalid &&
            (<div data-testid={key} className="la1-evt-upload-err-msg" key={key}>
              <i className="fa fa-exclamation-circle" />
              {message}
            </div>)
        })}
        <ModalFooter>
          <button onClick={handleSubmit} className={enableDone() ? "btn btn-primary" : "btn btn-primary la1-evt-upload-gray-btn"} disabled={!enableDone() || state.isAnalysisRunning || state.isChecksumTaskRunning} data-testid="upload-done-btn">
            {buttonContent()}
          </button>
        </ModalFooter>
      </div>
      <input accept=".mp4,video/mp4" onChange={handleFileChange} ref={fileRef} className="la1-evt-upload-hide-file-input" type="file" data-testid="file-input-upload" />
    </Modal>
  )
}

EventUploadModal.displayName = 'EventUplodadModal';

EventUploadModal.propTypes = {
  closeFunction: PropTypes.func.isRequired,
  customerId: PropTypes.string.isRequired,
  disableMp4BoxValidations: PropTypes.bool.isRequired,
};

export default EventUploadModal;
